// src/components/redemittel/RedemittelStats.jsx
// Statistics display component for Redemittel practice sessions
import React from 'react';
import { TrendingUp, Award, Target, Clock, BarChart3, CheckCircle } from 'lucide-react';

const RedemittelStats = ({
  stats,
  onContinue,
  examProvider,
  level,
  categoryName
}) => {
  // Calculate performance metrics
  const successRate = Math.round((stats.good + stats.easy) / Math.max(stats.studied, 1) * 100);
  const masteryRate = Math.round(stats.easy / Math.max(stats.studied, 1) * 100);
  const struggleRate = Math.round((stats.again + stats.hard) / Math.max(stats.studied, 1) * 100);

  // Performance message based on success rate
  const getPerformanceMessage = () => {
    if (successRate >= 80) return { text: 'Ausgezeichnet!', emoji: '🌟' };
    if (successRate >= 60) return { text: 'Gut gemacht!', emoji: '👍' };
    if (successRate >= 40) return { text: 'Weiter üben!', emoji: '💪' };
    return { text: 'Nicht aufgeben!', emoji: '🎯' };
  };

  const performanceMsg = getPerformanceMessage();

  return (
    <div className="max-w-2xl mx-auto">
      {/* Main Stats Card */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white">
          <div className="text-center">
            <div className="text-5xl mb-2">{performanceMsg.emoji}</div>
            <h2 className="text-2xl font-bold">{performanceMsg.text}</h2>
            <p className="mt-2 opacity-90">
              {categoryName} - {examProvider} {level}
            </p>
          </div>
        </div>

        {/* Performance Overview */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Phrases Studied */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <span className="text-xs text-blue-600 font-medium">Studiert</span>
              </div>
              <div className="text-3xl font-bold text-blue-600">{stats.studied}</div>
              <div className="text-sm text-gray-600">Phrasen</div>
            </div>

            {/* Success Rate */}
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <span className="text-xs text-green-600 font-medium">Erfolg</span>
              </div>
              <div className="text-3xl font-bold text-green-600">{successRate}%</div>
              <div className="text-sm text-gray-600">Erfolgsrate</div>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="space-y-3 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Detaillierte Bewertung:</h3>

            {/* Progress bars for each rating */}
            <div className="space-y-2">
              {/* Easy */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 w-20">Einfach</span>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full bg-blue-500 rounded-full transition-all duration-500"
                    style={{ width: `${(stats.easy / Math.max(stats.studied, 1)) * 100}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                    {stats.easy}
                  </span>
                </div>
              </div>

              {/* Good */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 w-20">Gut</span>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${(stats.good / Math.max(stats.studied, 1)) * 100}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                    {stats.good}
                  </span>
                </div>
              </div>

              {/* Hard */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 w-20">Schwer</span>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full bg-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${(stats.hard / Math.max(stats.studied, 1)) * 100}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                    {stats.hard}
                  </span>
                </div>
              </div>

              {/* Again */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 w-20">Nochmal</span>
                <div className="flex-1 bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="absolute left-0 top-0 h-full bg-red-500 rounded-full transition-all duration-500"
                    style={{ width: `${(stats.again / Math.max(stats.studied, 1)) * 100}%` }}
                  />
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium">
                    {stats.again}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Achievement Badges */}
          {successRate >= 80 && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-lg mb-6">
              <div className="flex items-center gap-3">
                <Award className="w-8 h-8 text-yellow-600" />
                <div>
                  <h4 className="font-semibold text-gray-800">Leistungsabzeichen erhalten!</h4>
                  <p className="text-sm text-gray-600">
                    {masteryRate >= 50
                      ? 'Meister der Redemittel! 🏆'
                      : 'Redemittel-Experte! 🥇'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tips based on performance */}
          {struggleRate > 50 && (
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h4 className="font-semibold text-gray-800 mb-2">💡 Lerntipps:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Konzentrieren Sie sich auf schwierige Phrasen</li>
                <li>• Verwenden Sie die Phrasen in eigenen Sätzen</li>
                <li>• Wiederholen Sie täglich für bessere Ergebnisse</li>
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onContinue}
              className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all transform hover:scale-105"
            >
              Weiter üben
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
            >
              Neue Kategorie
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats Summary */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow text-center">
          <Clock className="w-6 h-6 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Nächste Wiederholung</p>
          <p className="font-semibold">In 24 Stunden</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow text-center">
          <Target className="w-6 h-6 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Tagesziel</p>
          <p className="font-semibold">{stats.studied}/20</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow text-center">
          <CheckCircle className="w-6 h-6 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Gemeistert</p>
          <p className="font-semibold">{stats.easy} Phrasen</p>
        </div>
      </div>
    </div>
  );
};

export default RedemittelStats;